import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Lock, 
  Eye, 
  FileText, 
  Mail, 
  Phone, 
  MapPin,
  CheckCircle,
  AlertCircle,
  Globe,
  Database,
  UserCheck,
  Cookie,
  Trash2,
  Share2
} from 'lucide-react';
const PrivacyPolicy = () => {
  const sections = [
    {
      title: "Information We Collect",
      icon: <Database size={20} />,
      content: "We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and services, when you participate in activities on the Website or otherwise contact us. The personal information we collect may include:",
      points: [
        "Name and contact data (email address, phone number, mailing address)",
        "Educational information (class, subjects, academic interests)",
        "Communication preferences",
        "Technical data (IP address, browser type, device information)"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <Eye size={20} />,
      content: "We use the information we collect or receive to communicate with you, process your requests, and improve our services. Specifically, we may use your information to:",
      points: [
        "Process admission applications and inquiries",
        "Send you updates about school events and announcements",
        "Respond to your questions and concerns",
        "Improve our website and services",
        "Comply with legal obligations"
      ]
    },
    {
      title: "Information Sharing",
      icon: <Share2 size={20} />,
      content: "We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:",
      points: [
        "With your consent",
        "To comply with legal requirements",
        "To protect our rights and safety",
        "With service providers who assist in our operations"
      ]
    },
    {
      title: "Data Security",
      icon: <Lock size={20} />,
      content: "We implement appropriate technical and organizational measures to protect your personal information. These measures include:",
      points: [
        "Secure servers and encrypted data transmission",
        "Regular security assessments",
        "Access controls and authentication measures",
        "Employee training on data protection"
      ]
    },
    {
      title: "Your Rights",
      icon: <UserCheck size={20} />,
      content: "You have certain rights regarding your personal information, including:",
      points: [
        "Access to your personal information",
        "Correction of inaccurate information",
        "Deletion of your information",
        "Restriction of processing",
        "Data portability"
      ]
    },
    {
      title: "Cookies and Tracking",
      icon: <Cookie size={20} />,
      content: "We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.",
      points: [
        "Essential cookies for website functionality",
        "Analytics cookies to understand usage",
        "Preference cookies to remember your settings"
      ]
    },
    {
      title: "Children's Privacy",
      icon: <Shield size={20} />,
      content: "Our website is intended for general audience. We do not knowingly collect personal information from children under 13 without parental consent. If you believe we have collected information from a child under 13, please contact us.",
      points: []
    },
    {
      title: "Changes to This Policy",
      icon: <FileText size={20} />,
      content: "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'effective date' at the top.",
      points: []
    }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block"
          >
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
              <Shield size={16} />
              Privacy & Security
            </span>
          </motion.div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-5 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-red-500 to-blue-600 mx-auto mt-3 rounded-full"></div>
          <p className="text-gray-500 mt-3 text-sm">Last Updated: January 1, 2024</p>
          <p className="text-gray-500 text-sm">St. Xavier's Public School, Bela Varanasi</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-blue-500"
        >
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 mb-1">Introduction</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                St. Xavier's Public School ("we", "us", or "our") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website or interact with us. Please read this privacy policy carefully. 
                If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6"
        >
          <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Mail size={18} className="text-blue-600" />
            Contact Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-gray-400" />
              <span className="text-gray-600">Bela Cholapur Varanasi - 221101, UP</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-gray-400" />
              <span className="text-gray-600">+91 8601017877, +91 9450785369</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-gray-400" />
              <span className="text-gray-600">st.xavierbelavns@gmail.com</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-gray-400" />
              <span className="text-gray-600">www.stxaviersbela.com</span>
            </div>
          </div>
        </motion.div>
        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="border-b border-gray-100">
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-white">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <div className="text-blue-600">{section.icon}</div>
                  </div>
                  <h2 className="font-bold text-gray-800 text-lg">{section.title}</h2>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  {section.content}
                </p>
                {section.points.length > 0 && (
                  <ul className="space-y-2 mt-3">
                    {section.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-md p-5 mt-6 border border-gray-100"
        >
          <div className="flex items-start gap-3">
            <div className="bg-purple-100 p-2 rounded-full">
              <Trash2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-1">Data Retention</h3>
              <p className="text-sm text-gray-600">
                We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. 
                We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, 
                and enforce our legal agreements and policies.
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.65 }}
          className="bg-white rounded-2xl shadow-md p-5 mt-4 border border-gray-100"
        >
          <div className="flex items-start gap-3">
            <div className="bg-yellow-100 p-2 rounded-full">
              <Globe className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-1">Third-Party Links</h3>
              <p className="text-sm text-gray-600">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                or content of these third-party sites. We encourage you to read the privacy policies of every website you visit.
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="mt-8 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
            <Lock className="w-10 h-10 mx-auto mb-3 opacity-80" />
            <h3 className="text-xl font-bold mb-2">Your Privacy Matters</h3>
            <p className="text-blue-100 text-sm">
              By using our website, you consent to our Privacy Policy and agree to its terms.
              If you have any questions about this Privacy Policy, please contact us.
            </p>
            <div className="mt-4 flex justify-center gap-4 text-sm">
              <a href="/terms" className="text-yellow-300 hover:text-yellow-200 transition">Terms of Service</a>
              <span>•</span>
              <a href="/contact" className="text-yellow-300 hover:text-yellow-200 transition">Contact Us</a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;